/**
 * Types for Budget feature.
 * Used by Budget components and hooks.
 */

// ============================================================================
// Budget Summary (embedded in /api/me response)
// ============================================================================

/**
 * Budget summary for current user.
 * Embedded in CurrentUser response from GET /api/me
 */
export interface BudgetSummary {
  /** Amount used this month in USD */
  usedUsd: number
  /** Remaining budget in USD (null for unlimited) */
  remainingUsd: number | null
  /** Percentage of budget used (0-100+) */
  percentUsed: number
}

// ============================================================================
// Full Budget Details (from /api/me/budget)
// ============================================================================

/**
 * Full budget details from GET /api/me/budget endpoint.
 */
export interface MyBudgetResponse {
  /** Effective budget limit in USD (null for unlimited) */
  effectiveBudgetUsd: number | null
  /** Source of the budget: 'user_override' | 'role' | 'unlimited' */
  source: string
  /** Amount used this month in USD */
  usedUsd: number
  /** Remaining budget in USD (null for unlimited) */
  remainingUsd: number | null
  /** Percentage of budget used */
  percentUsed: number
  /** Whether user is in warning zone (80%+) */
  isWarning: boolean
  /** Budget reset date (ISO 8601) */
  resetDate: string
  /** Current billing month (YYYY-MM format) */
  currentMonth: string
}

// ============================================================================
// HTTP 402 Error Details
// ============================================================================

/**
 * Error details returned in HTTP 402 response body.
 */
export interface BudgetExceededDetails {
  /** Amount used in USD */
  usedUsd: number
  /** Budget limit in USD */
  budgetUsd: number
  /** When budget resets (ISO 8601) */
  resetDate: string
  /** Percentage used (100+) */
  percentUsed: number
}

/**
 * Full HTTP 402 error response structure.
 */
export interface BudgetExceededError {
  error: 'BudgetExceeded'
  message: string
  statusCode: 402
  details: BudgetExceededDetails
}

// ============================================================================
// Budget Status Helpers
// ============================================================================

/**
 * Budget status levels based on usage percentage.
 */
export type BudgetStatus = 'normal' | 'warning' | 'critical' | 'unlimited'

/**
 * Get budget status from percentage.
 */
export function getBudgetStatus(percentUsed: number, isUnlimited: boolean): BudgetStatus {
  if (isUnlimited) return 'unlimited'
  if (percentUsed >= 80) return 'critical'
  if (percentUsed >= 60) return 'warning'
  return 'normal'
}

/**
 * Color classes for budget status.
 */
export const BUDGET_STATUS_COLORS: Record<BudgetStatus, {
  text: string
  bg: string
  progress: string
}> = {
  normal: {
    text: 'text-green-600',
    bg: 'bg-green-100',
    progress: 'bg-green-600',
  },
  warning: {
    text: 'text-yellow-600',
    bg: 'bg-yellow-100',
    progress: 'bg-yellow-600',
  },
  critical: {
    text: 'text-red-600',
    bg: 'bg-red-100',
    progress: 'bg-red-600',
  },
  unlimited: {
    text: 'text-gray-500',
    bg: 'bg-gray-100',
    progress: 'bg-gray-400',
  },
}
